import type { AdminCoverageSection } from '../domain/types';
import { hasCartoApiKey, withCartoApiKey } from '../map/carto-basemap';
import type { CompletedSectionCreditAudit } from './door-credit';

export interface AnalyticsReportSummary {
  totalSections: number;
  totalDoors: number;
  unassignedSections: number;
  unassignedDoors: number;
  assignedSections: number;
  assignedDoors: number;
  completedSections: number;
  completedDoors: number;
  completionPercent: number;
  peopleParticipating: number;
}

export interface AnalyticsReportNeighborhoodRow {
  name: string;
  totalSections: number;
  unassignedSections: number;
  assignedSections: number;
  completedSections: number;
  totalDoors: number;
  completedDoors: number;
  completionPercent: number;
  observedHouses: number;
  observationPercent: number;
  noSoliciting: number;
  welcomingPercent: number;
  emptyLots: number;
  underConstruction: number;
}

export interface AnalyticsReportGroupRow {
  name: string;
  totalSections: number;
  reservedSections: number;
  assignedSections: number;
  completedSections: number;
  totalDoors: number;
  completedDoors: number;
  completionPercent: number;
  participants: number;
}

export interface AnalyticsReportPersonRow {
  name: string;
  groupName: string;
  connectedSections: number;
  unfinishedSections: number;
  completedSections: number;
  assignedDoors: number;
  completedDoors: number;
  completionPercent: number;
}

export interface AnalyticsPrintReport {
  title: string;
  seasonLabel: string;
  seasonStatus: 'ACTIVE' | 'CLOSED';
  seasonNote: string;
  generatedAt: string;
  summary: AnalyticsReportSummary;
  attentionItems: string[];
  neighborhoods: AnalyticsReportNeighborhoodRow[];
  groups: AnalyticsReportGroupRow[];
  people: AnalyticsReportPersonRow[];
  participantNames: string[];
  sections: AdminCoverageSection[];
  creditAudit: CompletedSectionCreditAudit[];
}

const STATUS_COLORS: Record<AdminCoverageSection['status'], string> = {
  UNASSIGNED: '#7f93a3',
  ASSIGNED: '#1673b8',
  COMPLETE: '#2f8a57'
};

export function openAnalyticsPrintReport(report: AnalyticsPrintReport): void {
  const reportWindow = window.open('', '_blank');
  if (!reportWindow) {
    window.alert('Safari blocked the report window. Allow pop-ups for this site, then try again.');
    return;
  }

  try { reportWindow.opener = null; } catch { /* Safari may block changing opener. */ }

  reportWindow.document.open();
  reportWindow.document.write(printReportHtml(report));
  reportWindow.document.close();
  reportWindow.focus();
}

function printReportHtml(report: AnalyticsPrintReport): string {
  const participantRows = report.participantNames.length
    ? report.participantNames.map((name, index) => `<tr><td>${index + 1}</td><td>${escapeHtml(name)}</td></tr>`).join('')
    : '<tr><td colspan="2">No participating names were recorded.</td></tr>';

  const neighborhoodRows = report.neighborhoods.map((row) => `<tr>
    <td>${escapeHtml(row.name)}</td>
    <td>${number(row.totalSections)}</td>
    <td>${number(row.unassignedSections)}</td>
    <td>${number(row.assignedSections)}</td>
    <td>${number(row.completedSections)}</td>
    <td>${number(row.totalDoors)}</td>
    <td>${number(row.completedDoors)} / ${number(row.totalDoors)} (${row.completionPercent.toFixed(1)}%)</td>
    <td>${row.observationPercent.toFixed(1)}%</td>
    <td>${number(row.noSoliciting)}</td>
    <td>${row.welcomingPercent.toFixed(1)}%</td>
    <td>${number(row.emptyLots)}</td>
    <td>${number(row.underConstruction)}</td>
  </tr>`).join('');

  const groupRows = report.groups.map((row) => `<tr>
    <td>${escapeHtml(row.name)}</td>
    <td>${number(row.totalSections)}</td>
    <td>${number(row.reservedSections)}</td>
    <td>${number(row.assignedSections)}</td>
    <td>${number(row.completedSections)}</td>
    <td>${number(row.totalDoors)}</td>
    <td>${number(row.completedDoors)} / ${number(row.totalDoors)} (${row.completionPercent.toFixed(1)}%)</td>
    <td>${number(row.participants)}</td>
  </tr>`).join('');

  const peopleRows = report.people.map((row) => `<tr>
    <td>${escapeHtml(row.name)}</td>
    <td>${escapeHtml(row.groupName || '—')}</td>
    <td>${number(row.connectedSections)}</td>
    <td>${number(row.unfinishedSections)}</td>
    <td>${number(row.completedSections)}</td>
    <td>${number(row.assignedDoors)}</td>
    <td>${number(row.completedDoors)}</td>
    <td>${number(row.completedDoors)} / ${number(row.assignedDoors)} (${row.completionPercent.toFixed(1)}%)</td>
  </tr>`).join('');

  const sectionRows = report.sections
    .slice()
    .sort((left, right) => left.neighborhoodName.localeCompare(right.neighborhoodName) || left.sectionNumber - right.sectionNumber)
    .map((section) => `<tr>
      <td>${escapeHtml(section.neighborhoodName)}</td>
      <td>${escapeHtml(sectionLabel(section))}</td>
      <td>${escapeHtml(statusLabel(section.status))}</td>
      <td>${number(section.doors)}</td>
      <td>${escapeHtml(section.groupName ?? '—')}</td>
      <td>${escapeHtml(section.assignedTo?.displayName ?? (section.reservedForGroup ? 'Reserved for group' : '—'))}</td>
      <td>${escapeHtml(section.teamMembers.map((member) => member.name).join(', ') || '—')}</td>
      <td>${escapeHtml(formatDate(section.assignedAt))}</td>
      <td>${escapeHtml(formatDate(section.completedAt))}</td>
      <td>${escapeHtml(section.completionComment ?? '—')}</td>
    </tr>`).join('');

  const creditAuditRows = report.creditAudit.map((row) => {
    const status = row.source === 'RECORDED_TEAM'
      ? 'Recorded team · verify names'
      : row.source === 'ASSIGNEE_FALLBACK'
        ? 'Assignee fallback · verify solo'
        : row.source === 'NO_CREDIT' ? 'Completed without personal credit' : 'Needs roster review';
    const split = row.participants.length
      ? row.participants.map((person) => `${person.name}: ${number(row.sharePerParticipant)} each`).join('; ')
      : 'No participant recorded; no personal credit allocated';
    return `<tr>
      <td>${escapeHtml(row.neighborhoodName)}</td>
      <td>${number(row.sectionNumber)}</td>
      <td>${number(row.doors)}</td>
      <td>${escapeHtml(row.participants.map((person) => person.name).join(', ') || '—')}</td>
      <td>${escapeHtml(split)}</td>
      <td>${escapeHtml(status)}</td>
    </tr>`;
  }).join('');
  const missingRosterCount = report.creditAudit.filter((row) => row.source === 'MISSING_ROSTER').length;
  const fallbackRosterCount = report.creditAudit.filter((row) => row.source === 'ASSIGNEE_FALLBACK').length;

  const attention = report.attentionItems.length
    ? report.attentionItems.map((item) => `<li>${escapeHtml(item)}</li>`).join('')
    : '<li>No urgent issues were identified from the published data.</li>';

  const archiveWarning = report.seasonStatus === 'CLOSED'
    ? `<div class="notice"><strong>Season closed:</strong> ${escapeHtml(report.seasonNote)}</div>`
    : '';

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(report.title)} - ${escapeHtml(report.seasonLabel)}</title>
  <style>${reportStyles()}</style>
</head>
<body>
  <div class="screen-actions">
    <div><strong>PDF report ready</strong><span>Use the print dialog and choose “Save as PDF.”</span></div>
    <button type="button" onclick="window.print()">Print / Save PDF</button>
    <button type="button" class="secondary" onclick="window.close()">Close</button>
  </div>

  <main class="report">
    <header class="report-header">
      <div>
        <div class="eyebrow">DESERT HILLS · BUCKEYE COVERAGE</div>
        <h1>${escapeHtml(report.title)}</h1>
        <p>${escapeHtml(report.seasonLabel)} · ${report.seasonStatus === 'ACTIVE' ? 'Current season' : 'Closed season'}</p>
      </div>
      <div class="report-meta">
        <strong>Generated</strong>
        <span>${escapeHtml(formatDateTime(report.generatedAt))}</span>
        <small>${escapeHtml(report.seasonNote)}</small>
      </div>
    </header>

    ${archiveWarning}

    <section>
      <h2>Whole-church summary</h2>
      <div class="metric-grid">
        ${metric('Published sections', report.summary.totalSections, `${number(report.summary.totalDoors)} doors`)}
        ${metric('Still available', report.summary.unassignedSections, `${number(report.summary.unassignedDoors)} doors`)}
        ${metric('Currently assigned', report.summary.assignedSections, `${number(report.summary.assignedDoors)} doors`)}
        ${metric('Completed', report.summary.completedSections, `${number(report.summary.completedDoors)} doors`)}
        ${metric('Doors completed', `${report.summary.completionPercent.toFixed(1)}%`, `${number(report.summary.completedDoors)} of ${number(report.summary.totalDoors)}`)}
        ${metric('People participating', report.summary.peopleParticipating, 'Unique assigned people and entered team members')}
      </div>
    </section>

    <section class="two-column">
      <div>
        <h2>Pastoral decision summary</h2>
        <ul class="attention-list">${attention}</ul>
      </div>
      <div>
        <h2>Published section overview</h2>
        ${sectionMapSvg(report.sections)}
        <div class="legend"><span><i style="background:#7f93a3"></i>Not assigned</span><span><i style="background:#1673b8"></i>Assigned</span><span><i style="background:#2f8a57"></i>Completed</span></div>
      </div>
    </section>

    <section class="page-break">
      <h2>Neighborhood performance</h2>
      <p class="section-note">Welcoming indicators apply only to neighborhoods. They measure the percentage of observed residential houses without a reported No Soliciting sign.</p>
      <div class="table-wrap"><table>
        <thead><tr><th>Neighborhood</th><th>Sections</th><th>Available</th><th>Assigned</th><th>Complete</th><th>Doors</th><th>Doors complete</th><th>Observed</th><th>No Soliciting</th><th>Welcoming</th><th>Empty lots</th><th>Construction</th></tr></thead>
        <tbody>${neighborhoodRows || '<tr><td colspan="12">No neighborhood data.</td></tr>'}</tbody>
      </table></div>
    </section>

    <section class="page-break">
      <h2>Small-group performance</h2>
      <p class="section-note">Group analytics show territory distribution, completion, and participation. Welcoming indicators are intentionally excluded.</p>
      <div class="table-wrap"><table>
        <thead><tr><th>Small group</th><th>Territory</th><th>Reserved</th><th>Assigned</th><th>Complete</th><th>Doors</th><th>Doors complete</th><th>Participants</th></tr></thead>
        <tbody>${groupRows || '<tr><td colspan="8">No small-group data.</td></tr>'}</tbody>
      </table></div>
    </section>

    <section class="page-break">
      <h2>People</h2>
      <p class="section-note">Completed door credit is divided evenly among the recorded team members. Assigned-door totals show the full maps assigned to that person.</p>
      <div class="table-wrap"><table>
        <thead><tr><th>Person</th><th>Small group</th><th>Sections</th><th>Unfinished</th><th>Complete</th><th>Full doors assigned</th><th>Credited doors complete</th><th>Sections complete %</th></tr></thead>
        <tbody>${peopleRows || '<tr><td colspan="8">No person data.</td></tr>'}</tbody>
      </table></div>
    </section>

    <section class="page-break">
      <h2>Completed-door credit audit</h2>
      <p class="section-note">This recalculates every completed section from its saved team roster. Recorded teams show each person's equal share. Assignee fallbacks and missing rosters need a human check because the database cannot infer who actually went.</p>
      <p class="section-note">${number(report.creditAudit.length)} completed sections · ${number(fallbackRosterCount)} using assignee-only fallback · ${number(missingRosterCount)} with no person recorded.</p>
      <div class="table-wrap"><table>
        <thead><tr><th>Neighborhood</th><th>Section</th><th>Map doors</th><th>Recorded participants</th><th>Calculated credit per person</th><th>Audit status</th></tr></thead>
        <tbody>${creditAuditRows || '<tr><td colspan="6">No completed sections to audit.</td></tr>'}</tbody>
      </table></div>
    </section>

    <section class="page-break">
      <h2>Participating names</h2>
      <div class="participant-grid"><table>
        <thead><tr><th>#</th><th>Name</th></tr></thead>
        <tbody>${participantRows}</tbody>
      </table></div>
    </section>

    <section class="page-break">
      <h2>Complete published-section inventory</h2>
      <p class="section-note">This table contains the published section status, ownership, team names, and dates represented in this report.</p>
      <div class="table-wrap"><table class="section-table">
        <thead><tr><th>Neighborhood</th><th>Section</th><th>Status</th><th>Doors</th><th>Group</th><th>Assigned person</th><th>Team members</th><th>Assigned</th><th>Completed</th><th>Volunteer comment</th></tr></thead>
        <tbody>${sectionRows || '<tr><td colspan="9">No published sections.</td></tr>'}</tbody>
      </table></div>
    </section>

    <section class="definitions page-break">
      <h2>Definitions and interpretation</h2>
      <dl>
        <dt>People participating</dt><dd>Every unique primary assignee and every unique recorded team member. Completed door credit is divided equally across the recorded team members.</dd>
        <dt>Personal door credit</dt><dd>For a completed section, its doors are split evenly across the saved team roster. If no roster was saved, the direct assignee receives a provisional solo credit and is flagged for review. A section with no roster or assignee receives no personal credit until its roster is corrected.</dd>
        <dt>Doors completed</dt><dd>Completed published doors divided by all published doors in the selected season.</dd>
        <dt>Neighborhood welcoming indicator</dt><dd>Observed residential houses without a reported No Soliciting sign. It is not a judgment about residents’ character and is not calculated for people or groups.</dd>
        <dt>Observation coverage</dt><dd>Observed residential houses divided by published residential doors.</dd>
        <dt>Live data source</dt><dd>Analytics use the same current published sections, assignment records, completion status, team members, and field reports used by the admin, leader, and volunteer pages.</dd>
      </dl>
    </section>

    <footer>Generated by Buckeye Coverage Analytics · ${escapeHtml(report.seasonLabel)}</footer>
  </main>

  <script>
    window.addEventListener('load', () => {
      window.setTimeout(() => window.print(), 350);
    });
  <\/script>
</body>
</html>`;
}

function metric(label: string, value: string | number, detail: string): string {
  return `<article><span>${escapeHtml(label)}</span><strong>${typeof value === 'number' ? number(value) : escapeHtml(value)}</strong><small>${escapeHtml(detail)}</small></article>`;
}

function sectionMapSvg(sections: AdminCoverageSection[]): string {
  const geometries = sections.filter((section) => section.geometry);
  const coordinates = geometries.flatMap((section) => geometryLines(section).flat());
  if (!coordinates.length) return '<div class="map-empty">No section geometry is available.</div>';

  let minLng = Infinity;
  let maxLng = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;
  for (const [lng, lat] of coordinates) {
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  }
  const width = 980;
  const height = 360;
  const padding = 24;
  const lngRange = Math.max(0.000001, maxLng - minLng);
  const latRange = Math.max(0.000001, maxLat - minLat);
  const project = ([lng, lat]: [number, number]): [number, number] => [
    padding + (lng - minLng) / lngRange * (width - padding * 2),
    padding + (maxLat - lat) / latRange * (height - padding * 2)
  ];

  const paths = geometries.flatMap((section) => geometryLines(section).map((line) => {
    const points = line.map((coordinate) => project(coordinate).map((value) => value.toFixed(1)).join(',')).join(' ');
    return `<polyline points="${points}" fill="none" stroke="${STATUS_COLORS[section.status]}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" opacity="0.88" />`;
  })).join('');

  const labels = neighborhoodCenters(geometries).map((item) => {
    const [x, y] = project(item.center);
    return `<g><rect x="${(x - 54).toFixed(1)}" y="${(y - 11).toFixed(1)}" width="108" height="22" rx="7" fill="white" fill-opacity="0.9" stroke="#d6e0e6"/><text x="${x.toFixed(1)}" y="${(y + 4).toFixed(1)}" text-anchor="middle" font-size="10" font-weight="700" fill="#17324d">${escapeHtml(shortLabel(item.name, 20))}</text></g>`;
  }).join('');

  return `<svg class="section-map" viewBox="0 0 ${width} ${height}" role="img" aria-label="Published section overview"><rect width="${width}" height="${height}" fill="#f5f7f8" />${paths}${labels}</svg>`;
}

function geometryLines(section: AdminCoverageSection): Array<Array<[number, number]>> {
  const geometry = section.geometry;
  if (!geometry) return [];
  if (geometry.type === 'LineString') return [geometry.coordinates as Array<[number, number]>];
  return geometry.coordinates as Array<Array<[number, number]>>;
}

function neighborhoodCenters(sections: AdminCoverageSection[]): Array<{ name: string; center: [number, number] }> {
  const points = new Map<string, Array<[number, number]>>();
  for (const section of sections) {
    const list = points.get(section.neighborhoodName) ?? [];
    for (const line of geometryLines(section)) {
      if (line.length) list.push(line[Math.floor(line.length / 2)]!);
    }
    points.set(section.neighborhoodName, list);
  }
  return Array.from(points.entries()).flatMap(([name, values]) => {
    if (!values.length) return [];
    const center: [number, number] = [
      values.reduce((sum, value) => sum + value[0], 0) / values.length,
      values.reduce((sum, value) => sum + value[1], 0) / values.length
    ];
    return [{ name, center }];
  });
}

function reportStyles(): string {
  return `
    :root { color-scheme: light; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif; color: #17324d; background: #eef2f4; }
    * { box-sizing: border-box; }
    body { margin: 0; background: #eef2f4; }
    .screen-actions { position: sticky; top: 0; z-index: 10; display: flex; align-items: center; gap: 10px; padding: 12px 20px; border-bottom: 1px solid #d8e2e8; background: rgba(255,255,255,.96); box-shadow: 0 5px 18px rgba(23,50,77,.08); }
    .screen-actions div { margin-right: auto; }
    .screen-actions strong, .screen-actions span { display: block; }
    .screen-actions span { margin-top: 2px; color: #617287; font-size: 12px; }
    button { min-height: 42px; padding: 9px 16px; border: 0; border-radius: 11px; background: #176fa8; color: white; font-weight: 800; cursor: pointer; }
    button.secondary { border: 1px solid #ccd8df; background: white; color: #17324d; }
    .report { width: min(1280px, calc(100% - 32px)); margin: 22px auto 60px; padding: 34px; border: 1px solid #d7e0e5; border-radius: 22px; background: white; box-shadow: 0 14px 38px rgba(23,50,77,.09); }
    .report-header { display: flex; justify-content: space-between; gap: 32px; padding-bottom: 22px; border-bottom: 3px solid #17324d; }
    .eyebrow { color: #176fa8; font-size: 11px; font-weight: 900; letter-spacing: .12em; }
    h1 { margin: 7px 0 5px; font-size: 34px; line-height: 1.05; }
    h2 { margin: 0 0 12px; font-size: 21px; }
    p { color: #52667a; }
    .report-header p { margin: 0; font-size: 15px; }
    .report-meta { min-width: 230px; text-align: right; }
    .report-meta strong, .report-meta span, .report-meta small { display: block; }
    .report-meta span { margin-top: 4px; font-size: 13px; }
    .report-meta small { margin-top: 8px; color: #64768a; line-height: 1.4; }
    .notice { margin: 18px 0; padding: 12px 15px; border: 1px solid #e4c46f; border-radius: 10px; background: #fff7d8; color: #6a5416; font-size: 12px; }
    section { margin-top: 25px; }
    .metric-grid { display: grid; grid-template-columns: repeat(6, minmax(0,1fr)); gap: 10px; }
    .metric-grid article { padding: 14px; border: 1px solid #dce5ea; border-radius: 13px; background: #f7fafb; }
    .metric-grid span, .metric-grid strong, .metric-grid small { display: block; }
    .metric-grid span { color: #617287; font-size: 9px; font-weight: 900; text-transform: uppercase; letter-spacing: .05em; }
    .metric-grid strong { margin-top: 5px; font-size: 23px; }
    .metric-grid small { margin-top: 6px; color: #617287; font-size: 10px; line-height: 1.35; }
    .two-column { display: grid; grid-template-columns: .72fr 1.28fr; gap: 20px; align-items: start; }
    .two-column > div { padding: 17px; border: 1px solid #dce5ea; border-radius: 14px; }
    .attention-list { margin: 0; padding-left: 20px; }
    .attention-list li { margin: 0 0 9px; color: #324b63; line-height: 1.4; }
    .section-map { display: block; width: 100%; height: auto; border: 1px solid #dce5ea; border-radius: 11px; }
    .map-empty { display: grid; min-height: 190px; place-items: center; border: 1px dashed #cad7de; color: #657689; }
    .legend { display: flex; gap: 15px; margin-top: 8px; color: #617287; font-size: 10px; }
    .legend span { display: inline-flex; align-items: center; gap: 5px; }
    .legend i { width: 9px; height: 9px; border-radius: 50%; }
    .section-note { margin: -4px 0 12px; font-size: 11px; line-height: 1.4; }
    .table-wrap { overflow: visible; }
    table { width: 100%; border-collapse: collapse; font-size: 9.5px; }
    th, td { padding: 6px 7px; border: 1px solid #dce5ea; vertical-align: top; text-align: left; }
    th { background: #edf4f7; color: #17324d; font-size: 8.5px; text-transform: uppercase; letter-spacing: .035em; }
    tbody tr:nth-child(even) { background: #f8fafb; }
    .participant-grid { max-width: 570px; }
    .section-table { font-size: 8.8px; }
    .definitions dl { display: grid; grid-template-columns: 230px 1fr; gap: 9px 16px; }
    .definitions dt { font-weight: 900; }
    .definitions dd { margin: 0; color: #52667a; line-height: 1.45; }
    footer { margin-top: 30px; padding-top: 12px; border-top: 1px solid #dce5ea; color: #718195; font-size: 9px; text-align: center; }
    .page-break { break-before: page; }
    @media (max-width: 900px) {
      .metric-grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
      .two-column { grid-template-columns: 1fr; }
      .report-header { flex-direction: column; }
      .report-meta { text-align: left; }
      .screen-actions { flex-wrap: wrap; }
      .screen-actions div { width: 100%; }
    }
    @media print {
      @page { size: Letter landscape; margin: .42in; }
      body { background: white; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .screen-actions { display: none !important; }
      .report { width: auto; margin: 0; padding: 0; border: 0; border-radius: 0; box-shadow: none; }
      .report-header { break-after: avoid; }
      section { break-inside: auto; }
      .metric-grid, .two-column, .notice { break-inside: avoid; }
      table { page-break-inside: auto; }
      thead { display: table-header-group; }
      tr { break-inside: avoid; break-after: auto; }
      h2 { break-after: avoid; }
      .page-break { break-before: page; }
      a { color: inherit; text-decoration: none; }
    }
  `;
}

function sectionLabel(section: AdminCoverageSection): string {
  return `Section ${section.sectionNumber}`;
}

function statusLabel(status: AdminCoverageSection['status']): string {
  if (status === 'COMPLETE') return 'Completed';
  if (status === 'ASSIGNED') return 'Assigned';
  return 'Not assigned';
}

function formatDate(value: string | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
}

function shortLabel(value: string, maximum: number): string {
  return value.length <= maximum ? value : `${value.slice(0, maximum - 1)}…`;
}

function number(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#39;',
    '"': '&quot;'
  })[character] ?? character);
}

export function openPersonUnfinishedMapsPrint(
  personName: string,
  unfinishedSections: AdminCoverageSection[],
  allSections: AdminCoverageSection[],
  mode: 'UNFINISHED' | 'UNSTARTED' = 'UNFINISHED'
): void {
  const reportWindow = window.open('', '_blank');
  if (!reportWindow) {
    window.alert('Safari blocked the map window. Allow pop-ups for this site, then try again.');
    return;
  }
  try { reportWindow.opener = null; } catch { /* Safari may block changing opener. */ }

  reportWindow.document.open();
  reportWindow.document.write(personMapsHtml(personName, unfinishedSections, allSections, mode));
  reportWindow.document.close();
  reportWindow.focus();
}

function personMapsHtml(
  personName: string,
  unfinishedSections: AdminCoverageSection[],
  allSections: AdminCoverageSection[],
  mode: 'UNFINISHED' | 'UNSTARTED'
): string {
  const isUnstarted = mode === 'UNSTARTED';
  const mapStatusTitle = isUnstarted ? 'ASSIGNED · NOT STARTED' : 'UNFINISHED ASSIGNED MAP';
  const mapStatusCopy = isUnstarted ? 'Ready to hand out' : 'Not yet complete';
  const titleSuffix = isUnstarted ? 'Unstarted Maps' : 'Unfinished Maps';
  const readyLabel = isUnstarted ? 'not-started map' : 'unfinished map';
  const inclusionCopy = isUnstarted
    ? `Only sections currently assigned to ${escapeHtml(personName)} that have not been started are included.`
    : `Only sections currently assigned to ${escapeHtml(personName)} and not completed are included.`;

  const pages = unfinishedSections.map((section, index) => `
    <section class="person-map-page ${index > 0 ? 'page-break' : ''}">
      <header class="person-map-header">
        <div>
          <div class="eyebrow">DESERT HILLS · BUCKEYE COVERAGE</div>
          <h1>${escapeHtml(personName)}</h1>
          <p>${escapeHtml(section.neighborhoodName)} · ${escapeHtml(sectionLabel(section))}</p>
        </div>
        <div class="person-map-meta">
          <strong>${number(section.doors)} doors</strong>
          <span>${escapeHtml(section.streetNames.join(', ') || 'Published route')}</span>
          <small>Assigned ${escapeHtml(formatDate(section.assignedAt))}</small>
        </div>
      </header>
      <div class="person-map-status"><strong>${mapStatusTitle}</strong><span>${isUnstarted ? mapStatusCopy : section.routeStatus === 'IN_PROGRESS' ? 'In progress' : mapStatusCopy}</span></div>
      ${singleSectionMap(section, allSections)}
      <div class="person-map-footer">
        <div><strong>Section</strong><span>${escapeHtml(sectionLabel(section))}</span></div>
        <div><strong>Neighborhood</strong><span>${escapeHtml(section.neighborhoodName)}</span></div>
        <div><strong>Streets</strong><span>${escapeHtml(section.streetNames.join(' · ') || '—')}</span></div>
        <div><strong>Doors</strong><span>${number(section.doors)}</span></div>
      </div>
    </section>
  `).join('');

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(personName)} - ${titleSuffix}</title>
  <style>${personMapStyles()}</style>
</head>
<body>
  <div class="screen-actions">
    <div><strong>${unfinishedSections.length} ${readyLabel}${unfinishedSections.length === 1 ? '' : 's'} ready</strong><span>${inclusionCopy}</span></div>
    <button type="button" onclick="window.print()">Print maps</button>
    <button type="button" class="secondary" onclick="window.close()">Close</button>
  </div>
  <main>${pages}</main>
  <script>
    window.addEventListener('load', () => {
      let printed = false;
      const printOnce = () => {
        if (printed) return;
        printed = true;
        window.print();
      };
      const images = Array.from(document.images);
      const ready = images.map((image) => image.complete ? Promise.resolve() : new Promise((resolve) => { image.addEventListener('load', resolve, { once: true }); image.addEventListener('error', resolve, { once: true }); }));
      Promise.all(ready).then(() => window.setTimeout(printOnce, 250));
      window.setTimeout(printOnce, 2200);
    });
  <\/script>
</body>
</html>`;
}

function singleSectionMap(target: AdminCoverageSection, allSections: AdminCoverageSection[]): string {
  const targetCoordinates = geometryLines(target).flat();
  if (!targetCoordinates.length) return '<div class="person-map-empty">No route geometry is available for this section.</div>';

  let minLng = Math.min(...targetCoordinates.map(([lng]) => lng));
  let maxLng = Math.max(...targetCoordinates.map(([lng]) => lng));
  let minLat = Math.min(...targetCoordinates.map(([, lat]) => lat));
  let maxLat = Math.max(...targetCoordinates.map(([, lat]) => lat));
  const lngRange = Math.max(0.0008, maxLng - minLng);
  const latRange = Math.max(0.0008, maxLat - minLat);
  minLng -= lngRange * 0.55;
  maxLng += lngRange * 0.55;
  minLat -= latRange * 0.55;
  maxLat += latRange * 0.55;

  const width = 1000;
  const height = 620;
  const zoom = chooseStaticMapZoom(minLng, minLat, maxLng, maxLat, width, height);
  const topLeft = staticMapTopLeft(minLng, minLat, maxLng, maxLat, zoom, width, height);
  const project = ([lng, lat]: [number, number]): [number, number] => {
    const point = webMercatorPixel(lng, lat, zoom);
    return [point.x - topLeft.x, point.y - topLeft.y];
  };

  const visibleBounds = staticMapGeoBounds(topLeft.x, topLeft.y, width, height, zoom);
  const intersects = (section: AdminCoverageSection): boolean => geometryLines(section).some((line) => line.some(([lng, lat]) =>
    lng >= visibleBounds.minLng && lng <= visibleBounds.maxLng && lat >= visibleBounds.minLat && lat <= visibleBounds.maxLat
  ));
  const context = allSections.filter((section) =>
    section.neighborhoodId === target.neighborhoodId && section.id !== target.id && section.geometry && intersects(section)
  );

  const tiles = staticMapTiles(topLeft.x, topLeft.y, width, height, zoom, 'voyager_nolabels');
  const labels = staticMapTiles(topLeft.x, topLeft.y, width, height, zoom, 'voyager_only_labels');
  const contextPaths = context.flatMap((section) => geometryLines(section).map((line) => {
    const points = line.map((coordinate) => project(coordinate).map((value) => value.toFixed(1)).join(',')).join(' ');
    return `<polyline points="${points}" fill="none" stroke="#6f8493" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" opacity="0.48" />`;
  })).join('');
  const targetPaths = geometryLines(target).map((line) => {
    const points = line.map((coordinate) => project(coordinate).map((value) => value.toFixed(1)).join(',')).join(' ');
    return `<polyline points="${points}" fill="none" stroke="#176fa8" stroke-width="10" stroke-linecap="round" stroke-linejoin="round" opacity="0.96" />`;
  }).join('');

  const center: [number, number] = [
    targetCoordinates.reduce((sum, coordinate) => sum + coordinate[0], 0) / targetCoordinates.length,
    targetCoordinates.reduce((sum, coordinate) => sum + coordinate[1], 0) / targetCoordinates.length
  ];
  const [cx, cy] = project(center);
  const streetLabel = shortLabel(target.streetNames.join(' · ') || sectionLabel(target), 58);

  return `<div class="person-static-map" style="--map-width:${width}px;--map-height:${height}px">
    <div class="person-map-tiles" aria-hidden="true">${tiles}</div>
    <svg class="person-map-overlay" viewBox="0 0 ${width} ${height}" role="img" aria-label="${escapeHtml(sectionLabel(target))} route map">
      ${contextPaths}
      ${targetPaths}
    </svg>
    <div class="person-map-labels" aria-hidden="true">${labels}</div>
    <svg class="person-map-annotations" viewBox="0 0 ${width} ${height}" aria-hidden="true">
      <g transform="translate(${cx.toFixed(1)} ${cy.toFixed(1)})">
        <rect x="-118" y="-27" width="236" height="54" rx="14" fill="white" fill-opacity="0.96" stroke="#b8c7d0" stroke-width="1.5" />
        <text x="0" y="-4" text-anchor="middle" font-size="16" font-weight="800" fill="#17324d">${escapeHtml(sectionLabel(target))}</text>
        <text x="0" y="15" text-anchor="middle" font-size="11" font-weight="650" fill="#536b7d">${escapeHtml(streetLabel)}</text>
      </g>
      <g transform="translate(${width - 62} 62)">
        <rect x="-25" y="-37" width="50" height="78" rx="15" fill="white" fill-opacity="0.88" stroke="#c4d0d7" />
        <path d="M0 22V-20 M0-20L-8-7 M0-20L8-7" fill="none" stroke="#17324d" stroke-width="3" stroke-linecap="round" />
        <text x="0" y="35" text-anchor="middle" font-size="13" font-weight="900" fill="#17324d">N</text>
      </g>
    </svg>
    <div class="person-map-attribution">© OpenStreetMap contributors${hasCartoApiKey() ? ' © CARTO' : ''} · Gray = nearby published sections · Blue = assigned section</div>
  </div>`;
}

function chooseStaticMapZoom(minLng: number, minLat: number, maxLng: number, maxLat: number, width: number, height: number): number {
  for (let zoom = 19; zoom >= 13; zoom -= 1) {
    const nw = webMercatorPixel(minLng, maxLat, zoom);
    const se = webMercatorPixel(maxLng, minLat, zoom);
    if (se.x - nw.x <= width * 0.88 && se.y - nw.y <= height * 0.84) return zoom;
  }
  return 13;
}

function staticMapTopLeft(minLng: number, minLat: number, maxLng: number, maxLat: number, zoom: number, width: number, height: number): { x: number; y: number } {
  const nw = webMercatorPixel(minLng, maxLat, zoom);
  const se = webMercatorPixel(maxLng, minLat, zoom);
  return { x: (nw.x + se.x) / 2 - width / 2, y: (nw.y + se.y) / 2 - height / 2 };
}

function staticMapTiles(left: number, top: number, width: number, height: number, zoom: number, variant: 'voyager_nolabels' | 'voyager_only_labels'): string {
  // No key: use one labeled tile layer under the routes; do not request
  // CARTO's HTTP-200 watermarked tiles on printed maps either.
  if (!hasCartoApiKey() && variant === 'voyager_only_labels') return '';
  const tileSize = 256;
  const firstX = Math.floor(left / tileSize);
  const lastX = Math.floor((left + width) / tileSize);
  const firstY = Math.floor(top / tileSize);
  const lastY = Math.floor((top + height) / tileSize);
  const maxTile = 2 ** zoom;
  const tiles: string[] = [];
  for (let y = firstY; y <= lastY; y += 1) {
    if (y < 0 || y >= maxTile) continue;
    for (let x = firstX; x <= lastX; x += 1) {
      const wrappedX = ((x % maxTile) + maxTile) % maxTile;
      const screenX = x * tileSize - left;
      const screenY = y * tileSize - top;
      const subdomain = ['a', 'b', 'c', 'd'][Math.abs(x + y) % 4];
      const tileUrl = hasCartoApiKey()
        ? withCartoApiKey(`https://${subdomain}.basemaps.cartocdn.com/rastertiles/${variant}/${zoom}/${wrappedX}/${y}@2x.png`)
        : `https://tile.openstreetmap.org/${zoom}/${wrappedX}/${y}.png`;
      tiles.push(`<img class="person-map-tile" alt="" src="${tileUrl}" style="left:${screenX.toFixed(1)}px;top:${screenY.toFixed(1)}px" />`);
    }
  }
  return tiles.join('');
}

function staticMapGeoBounds(left: number, top: number, width: number, height: number, zoom: number): { minLng: number; minLat: number; maxLng: number; maxLat: number } {
  const nw = webMercatorLatLng(left, top, zoom);
  const se = webMercatorLatLng(left + width, top + height, zoom);
  return { minLng: nw.lng, maxLng: se.lng, minLat: se.lat, maxLat: nw.lat };
}

function webMercatorPixel(lng: number, lat: number, zoom: number): { x: number; y: number } {
  const scale = 256 * 2 ** zoom;
  const clippedLat = Math.max(-85.05112878, Math.min(85.05112878, lat));
  const sinLat = Math.sin(clippedLat * Math.PI / 180);
  return {
    x: (lng + 180) / 360 * scale,
    y: (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale
  };
}

function webMercatorLatLng(x: number, y: number, zoom: number): { lng: number; lat: number } {
  const scale = 256 * 2 ** zoom;
  const lng = x / scale * 360 - 180;
  const n = Math.PI - 2 * Math.PI * y / scale;
  const lat = 180 / Math.PI * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
  return { lng, lat };
}

function personMapStyles(): string {
  return `
    :root { color-scheme: light; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif; color: #17324d; background: #edf2f4; }
    * { box-sizing: border-box; }
    body { margin: 0; background: #edf2f4; }
    .screen-actions { position: sticky; top: 0; z-index: 20; display: flex; align-items: center; gap: 10px; padding: 12px 20px; border-bottom: 1px solid #d7e2e8; background: rgba(255,255,255,.96); box-shadow: 0 5px 18px rgba(23,50,77,.08); }
    .screen-actions div { margin-right: auto; }
    .screen-actions strong, .screen-actions span { display: block; }
    .screen-actions span { margin-top: 2px; color: #617287; font-size: 12px; }
    button { min-height: 42px; padding: 9px 16px; border: 0; border-radius: 11px; background: #176fa8; color: white; font-weight: 800; cursor: pointer; }
    button.secondary { border: 1px solid #ccd8df; background: white; color: #17324d; }
    main { width: min(1100px, calc(100% - 28px)); margin: 20px auto 54px; }
    .person-map-page { padding: 30px; border: 1px solid #d7e0e5; border-radius: 22px; background: white; box-shadow: 0 14px 38px rgba(23,50,77,.08); }
    .person-map-header { display: flex; justify-content: space-between; gap: 28px; align-items: start; padding-bottom: 17px; border-bottom: 3px solid #17324d; }
    .eyebrow { color: #176fa8; font-size: 11px; font-weight: 900; letter-spacing: .12em; }
    h1 { margin: 6px 0 3px; font-size: 32px; }
    p { margin: 0; color: #52667a; }
    .person-map-meta { max-width: 420px; text-align: right; }
    .person-map-meta strong, .person-map-meta span, .person-map-meta small { display: block; }
    .person-map-meta strong { font-size: 21px; }
    .person-map-meta span { margin-top: 4px; color: #415a70; font-weight: 700; }
    .person-map-meta small { margin-top: 6px; color: #6c7e8e; }
    .person-map-status { display: flex; justify-content: space-between; gap: 14px; margin: 15px 0 12px; padding: 10px 13px; border-radius: 12px; background: #eaf4fb; color: #175f91; font-size: 12px; }
    .person-static-map { position: relative; width: 100%; aspect-ratio: 1000 / 620; overflow: hidden; border: 1px solid #dce5ea; border-radius: 16px; background: #f7f8f5; }
    .person-map-tiles, .person-map-overlay, .person-map-labels, .person-map-annotations { position: absolute; inset: 0; width: 100%; height: 100%; }
    .person-map-tiles { overflow: hidden; background: #eef2ef; }
    .person-map-tile { position: absolute; width: 256px; height: 256px; object-fit: cover; }
    .person-map-overlay { z-index: 2; }
    .person-map-labels { z-index: 3; pointer-events: none; overflow: hidden; }
    .person-map-annotations { z-index: 4; pointer-events: none; }
    .person-map-attribution { position: absolute; z-index: 5; right: 7px; bottom: 5px; padding: 3px 6px; border-radius: 6px; background: rgba(255,255,255,.86); color: #617287; font-size: 8px; }
    .person-map-empty { display: grid; min-height: 460px; place-items: center; border: 1px dashed #cad7de; border-radius: 16px; color: #657689; }
    .person-map-footer { display: grid; grid-template-columns: .8fr 1.2fr 2fr .6fr; gap: 9px; margin-top: 12px; }
    .person-map-footer > div { padding: 10px 12px; border: 1px solid #dce5ea; border-radius: 11px; background: #f7fafb; }
    .person-map-footer strong, .person-map-footer span { display: block; }
    .person-map-footer strong { color: #657689; font-size: 9px; text-transform: uppercase; letter-spacing: .05em; }
    .person-map-footer span { margin-top: 4px; font-size: 12px; font-weight: 700; }
    .page-break { margin-top: 22px; }
    @media print {
      @page { size: Letter landscape; margin: .35in; }
      body { background: white; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .screen-actions { display: none !important; }
      main { width: auto; margin: 0; }
      .person-map-page { padding: 0; border: 0; border-radius: 0; box-shadow: none; break-inside: avoid; }
      .person-static-map { height: 5.65in; aspect-ratio: auto; }
      .page-break { margin-top: 0; break-before: page; }
    }
  `;
}
